import { http } from 'shared/utils/axios';

export interface IWorkflowNode {
  id?: string;
  _id?: string;
  workflow: string;
  key: string;
  question?: {
    text: string;
    options: Array<{
      label: string;
      value: string;
      nextNode: string | null;
      action?: {
        type: string;
        parameters?: Record<string, any>;
      };
    }>;
  };
  action?: {
    type: string;
    parameters?: Record<string, any>;
  };
  nextNode?: string | null;
  isStartNode: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkflow {
  id?: string;
  _id?: string;
  name: string;
  description?: string;
  affiliate?: string | null;
  active: boolean;
  version: number;
  startNode: string;
  nodes?: IWorkflowNode[];
  createdAt: string;
  updatedAt: string;
}

export interface IGetCurrentWorkflowResponse {
  success: boolean;
  workflow: IWorkflow | null;
  conversationId: string;
  message?: string;
}

export interface IGetCurrentWorkflowParams {
  affiliateId?: string;
  conversationId?: string;
}

export interface IWorkflowExecution {
  id?: string;
  _id?: string;
  workflow: string;
  patient: string;
  patientConversation: string;
  currentNode: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  answers?: any[];
}

export interface IStartWorkflowResponse {
  execution: IWorkflowExecution;
  currentNode: IWorkflowNode;
  message: string;
  isExisting?: boolean;
}

export interface ISubmitAnswerRequest {
  nodeId: string;
  selectedOption: {
    label: string;
    value: string;
  };
}

export interface ISubmitAnswerResponse {
  execution: IWorkflowExecution;
  actionResult: any;
  nextNode: IWorkflowNode | null;
  isCompleted: boolean;
  message?: string;
}

class WorkflowService {
  private static instance: WorkflowService;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): WorkflowService {
    if (!WorkflowService.instance) {
      WorkflowService.instance = new WorkflowService();
    }
    return WorkflowService.instance;
  }

  /**
   * Get current workflow for the authenticated patient
   * @param params - Optional affiliateId and conversationId
   * @returns Promise with workflow data
   */
  async getCurrentWorkflow(
    params?: IGetCurrentWorkflowParams
  ): Promise<IGetCurrentWorkflowResponse> {
    try {
      const response = await http<IGetCurrentWorkflowResponse>({
        url: '/workflows/current',
        method: 'GET',
        params: {
          affiliateId: params?.affiliateId || undefined,
          conversationId: params?.conversationId || undefined
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching current workflow:', error);
      throw error;
    }
  }

  /**
   * Start a workflow execution
   * @param workflowId - The workflow ID to start
   * @param startNodeId - The start node ID
   * @param conversationId - The patient conversation ID
   * @returns Promise with execution data
   */
  async startWorkflow(
    workflowId: string,
    startNodeId: string,
    conversationId: string
  ): Promise<IStartWorkflowResponse> {
    try {
      const response = await http<IStartWorkflowResponse>({
        url: '/workflows/start',
        method: 'POST',
        data: {
          workflowId,
          startNodeId,
          conversationId
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error starting workflow:', error);
      throw error;
    }
  }

  /**
   * Submit an answer to a workflow question
   * @param executionId - The workflow execution ID
   * @param nodeId - The current node ID
   * @param selectedOption - The selected option
   * @returns Promise with next node data and action result
   */
  async submitAnswer(
    executionId: string,
    nodeId: string,
    selectedOption: { label: string; value: string }
  ): Promise<ISubmitAnswerResponse> {
    try {
      const response = await http<ISubmitAnswerResponse>({
        url: `/workflows/${executionId}/answer`,
        method: 'POST',
        data: {
          nodeId,
          selectedOption
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error submitting answer:', error);
      throw error;
    }
  }
}

export default WorkflowService.getInstance();
